import { Fragment, useRef, type ReactNode } from 'react';
import { t } from '../i18n/index.js';
import { cn } from '../lib/cn.js';
import { Banner } from '../ui/Banner.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card.js';
import { EmptyState, type EmptyReason } from '../ui/EmptyState.js';
import { Button } from '../ui/Button.js';
import { TableControls } from './TableControls.js';
import type { TableView } from './table-view.js';
import { IconArrowUp } from '../ui/Icon.js';
import { Pagination } from '../ui/Pagination.js';
import { TableSkeleton } from '../ui/Skeleton.js';
import {
  Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, TableWrap,
} from '../ui/Table.js';

/**
 * DataTable — a table with its toolbar, states and pagination.
 *
 * ── WHY THIS EXISTS ───────────────────────────────────────────────────────
 *
 * Every list screen needs the same six things and they are always the same
 * six: a header, a filter row, column definitions, the four states (loading,
 * error, empty, populated) and pagination. In v1 each page rebuilt them, which
 * is why the empty states differed page to page and the loading states mostly
 * did not exist.
 *
 * Columns are DATA here, not JSX. That is what lets alignment, tabular figures
 * and width be decided once per column rather than repeated in every cell — and
 * it is what will make column visibility, reordering and CSV export a change to
 * this file rather than to eleven pages.
 *
 * ── THE STATES ARE NOT INTERCHANGEABLE ────────────────────────────────────
 *
 * `loading`, `error` and empty are handled here so a page cannot forget one.
 * Empty in particular takes a REASON, because in an accounting product "no data
 * yet" and "your filter excluded everything" and "you may not see this" are
 * three different sentences and only one of them means the business has no
 * activity.
 */

export interface Column<Row> {
  /** Stable key. Also the React key for the cell. */
  id: string;
  header: ReactNode;
  /** Renders the cell. Given the row and its index. */
  cell: (row: Row, index: number) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Stops the cell wrapping — right for codes and amounts, wrong for prose. */
  nowrap?: boolean;
  /** A fixed width, for columns that should not flex (actions, checkboxes). */
  width?: string;
  /** Hides the column below `lg`. Use for anything that is not load-bearing. */
  secondary?: boolean;
  /** Column header only; no accessible name needed for an actions column. */
  headerHidden?: boolean;

  /**
   * Makes the column sortable. Returns the RAW comparable — minor units, an ISO
   * date, a lowercase code — never what `cell` renders.
   *
   * Sorting the rendered value is the classic mistake: `<Money>` produces
   * "₹1,72,717.67", and ordering that string ranks a lakh below ten thousand
   * because it compares "1," against "9,". Same for "8 Aug 2026".
   */
  sort?: (row: Row) => string | number | null | undefined;

  /** Text this column contributes to the table's search. */
  search?: (row: Row) => string | null | undefined;

  /** Set by useTableView. Do not pass by hand. */
  sortDirection?: 'asc' | 'desc' | null;
  /** Set by useTableView. Do not pass by hand. */
  onSort?: () => void;
}

export interface DataTableProps<Row> {
  /**
   * A `useTableView` result. Supplies rows, sortable columns, the toolbar
   * controls and pagination in one prop, so a page wires search + filter + sort
   * + paging by passing this and nothing else.
   *
   * `columns`, `rows` and `pagination` are still accepted directly for tables
   * that genuinely cannot grow — a five-row summary needs none of this, and
   * making it carry the machinery anyway is how a design system starts feeling
   * heavy.
   */
  view?: TableView<Row>;
  title?: ReactNode;
  description?: ReactNode;
  /** Filters, search, view switches. Rendered above the table. */
  toolbar?: ReactNode;
  /** Buttons in the card header, beside the title. */
  actions?: ReactNode;

  columns?: Array<Column<Row>>;
  rows?: Row[];
  rowKey: (row: Row, index: number) => string;

  loading?: boolean;
  error?: string | null;
  /** Retry handler shown on the error state. */
  onRetry?: () => void;

  /** Why the table is empty. Required when it can be — see the note above. */
  emptyReason?: EmptyReason;
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;

  onRowClick?: (row: Row, index: number) => void;
  isRowSelected?: (row: Row, index: number) => boolean;
  isRowMuted?: (row: Row, index: number) => boolean;
  /** An expanded panel under a row — voucher lines, a settlement breakdown. */
  renderExpanded?: (row: Row, index: number) => ReactNode;
  expandedKey?: string | null;

  pagination?: {
    page: number;
    pageCount: number;
    onPage: (page: number) => void;
    totalItems?: number;
    itemNoun?: string;
    previousLabel?: string;
    nextLabel?: string;
  };
  /** Rendered under the table, above pagination — totals, notes. */
  footer?: ReactNode;
  className?: string;
}

export function DataTable<Row>({
  title,
  description,
  toolbar: toolbarProp,
  actions,
  view,
  columns: columnsProp,
  rows: rowsProp,
  rowKey,
  loading = false,
  error = null,
  onRetry,
  emptyReason: emptyReasonProp = 'nothing-yet',
  emptyTitle: emptyTitleProp = t('table.nothingToShow'),
  emptyDescription: emptyDescriptionProp,
  emptyAction: emptyActionProp,
  onRowClick,
  isRowSelected,
  isRowMuted,
  renderExpanded,
  expandedKey,
  pagination: paginationProp,
  footer,
  className,
}: DataTableProps<Row>) {
  // The view wins where it is given, so a page cannot accidentally pass both a
  // view and a stale row list and render the unfiltered one.
  const columns = view?.columns ?? columnsProp ?? [];
  const rows = view?.rows ?? rowsProp ?? [];
  const pagination = view?.pagination ?? paginationProp;

  /**
   * What the current rows ARE, cheaply.
   *
   * The keys and the count, not the contents: a signature over every cell would
   * cost more than the animation is worth on a 50-row page, and would also fire
   * when a figure updates in place — which `Money` already handles with its own
   * crossfade. This is the "you are looking at a different set of rows" signal:
   * a new page, a different sort, a filter that actually excluded something.
   */
  const rowSignature = `${rows.length}:${rows.map((r, i) => rowKey(r, i)).join('|')}`;

  /**
   * The signature of the first row set this table ever showed.
   *
   * Remembering the SIGNATURE and not a boolean is what keeps the class stable
   * for the life of that `<tbody>`. A boolean flips on the very next render —
   * a hover, a parent state change, anything — and swapping the class mid-flight
   * cancels the cascade a third of the way through, which looks worse than no
   * animation at all. Keyed the same way the element is, so the two agree.
   *
   * A ref, not state: it must not cause a render of its own, and it is read
   * during the same render that sets it.
   */
  const firstRowSet = useRef<string | null>(null);
  if (rows.length > 0 && firstRowSet.current === null) firstRowSet.current = rowSignature;
  const firstLoad = rows.length > 0 && firstRowSet.current === rowSignature;
  // COMPOSED, not replaced. A page can have controls the engine knows nothing
  // about — a "show archived" toggle, a segmented preset — and having `view`
  // quietly swallow them removed a working control from Returns the moment that
  // page was wired up.
  const toolbar =
    view && toolbarProp ? (
      <>
        <TableControls view={view} />
        {toolbarProp}
      </>
    ) : view ? (
      <TableControls view={view} />
    ) : (
      toolbarProp
    );
  // A filter that matched nothing is NOT the same claim as "nothing recorded
  // yet". Told the wrong one, a seller concludes their books are empty when they
  // have simply over-filtered — so the reason, the wording and the way out all
  // change together. The page's own empty copy is about an empty ledger and
  // would be actively misleading here, which is why it is replaced rather than
  // supplemented.
  const filteredToNothing = !!view && view.activeFilterCount > 0 && view.matchCount === 0;
  const emptyReason: EmptyReason = filteredToNothing ? 'filtered-out' : emptyReasonProp;
  const emptyTitle = filteredToNothing ? t('table.filteredEmptyTitle') : emptyTitleProp;
  const emptyDescription = filteredToNothing
    ? t('table.filteredEmptyBody', {
        count: view.totalCount,
        total: view.totalCount.toLocaleString('en-IN'),
      })
    : emptyDescriptionProp;
  const emptyAction = filteredToNothing ? (
    <Button onClick={view.clearAll}>{t('table.clearFilters')}</Button>
  ) : (
    emptyActionProp
  );

  const visible = columns.filter((c) => !c.secondary);
  const colSpan = columns.length;

  return (
    <Card className={className}>
      {(title || description || actions) && (
        <CardHeader actions={actions} bordered={!toolbar}>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}

      {toolbar && (
        <div className="flex flex-wrap items-center gap-2 border-b border-subtle px-(--card-padding) py-3">
          {toolbar}
        </div>
      )}

      {/* Error takes precedence over everything. A table rendered beside an
          error message implies the rows are current, and they are not. */}
      {error ? (
        <CardContent>
          <Banner
            intent="danger"
            title={t('shell.couldNotLoad')}
            actions={
              onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="text-md font-medium underline underline-offset-2"
                >
                  {t('table.retry')}
                </button>
              )
            }
          >
            {error}
          </Banner>
        </CardContent>
      ) : loading ? (
        <TableSkeleton rows={6} columns={Math.min(visible.length, 6)} />
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          reason={emptyReason}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      ) : (
        <TableWrap>
          <Table>
            <TableHead>
              <tr>
                {columns.map((column) => (
                  <TableHeaderCell
                    key={column.id}
                    align={column.align}
                    style={column.width ? { width: column.width } : undefined}
                    className={cn(column.secondary && 'hidden lg:table-cell')}
                    aria-sort={
                      !column.onSort ? undefined
                      : column.sortDirection === 'asc' ? 'ascending'
                      : column.sortDirection === 'desc' ? 'descending'
                      : 'none'
                    }
                  >
                    {/* An actions column has no meaningful header, but the cell
                        still needs one for screen readers — hence the visually
                        hidden variant rather than an empty <th>. */}
                    {column.headerHidden ? (
                      <span className="sr-only">{column.header}</span>
                    ) : column.onSort ? (
                      <SortButton column={column} />
                    ) : (
                      column.header
                    )}
                  </TableHeaderCell>
                ))}
              </tr>
            </TableHead>
            {/* Keyed on what the rows ARE, so the fade runs when the result
                set changes and not when a re-render happens to reproduce the
                same rows. Typing a search that matches nothing new animates
                nothing. */}
            <TableBody
              key={rowSignature}
              className={firstLoad ? 'rows-enter-motion' : 'rows-motion'}
            >
              {rows.map((row, index) => {
                const key = rowKey(row, index);
                const expanded = expandedKey != null && expandedKey === key;
                return (
                  <Fragment key={key}>
                    <TableRow
                      interactive={Boolean(onRowClick)}
                      selected={isRowSelected?.(row, index) ?? expanded}
                      muted={isRowMuted?.(row, index)}
                      onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          nowrap={column.nowrap}
                          className={cn(column.secondary && 'hidden lg:table-cell')}
                        >
                          {column.cell(row, index)}
                        </TableCell>
                      ))}
                    </TableRow>
                    {expanded && renderExpanded && (
                      <tr className="border-b border-(--table-border)">
                        <td colSpan={colSpan} className="bg-(--table-nested-bg) p-0">
                          {/* Fade and lift, not a height animation: the panel
                              can be a voucher's lines or a whole settlement
                              breakdown, and every height technique needs either
                              a cap that clips or a measurement pass. */}
                          <div className="panel-motion">{renderExpanded(row, index)}</div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableWrap>
      )}

      {footer && !loading && !error && rows.length > 0 && (
        <div className="border-t border-subtle px-(--card-padding) py-3">{footer}</div>
      )}

      {pagination && !loading && !error && rows.length > 0 && (
        <div className="border-t border-subtle">
          <Pagination {...pagination} />
        </div>
      )}
    </Card>
  );
}

/**
 * The toolbar row: filters on the left, search and view controls on the right.
 *
 * Matching both references — the ERP shot puts "Status: All Invoice" left with
 * search and Filters right, and it is the arrangement people expect because
 * filters change WHAT is listed while search narrows within it.
 */
/**
 * A sortable column header.
 *
 * `aria-sort` on the <th> is what a screen reader announces, and it belongs on
 * the cell rather than the button — which is why this renders the button and the
 * parent sets the attribute.
 *
 * The arrow is always in the layout, at low opacity when the column is not the
 * sorted one. Showing it only on the active column makes every other header
 * shift sideways by the arrow's width the moment you sort, and a table whose
 * headers move as you use it feels unreliable.
 */
function SortButton<Row>({ column }: { column: Column<Row> }) {
  const direction = column.sortDirection ?? null;
  return (
    <button
      type="button"
      onClick={column.onSort}
      className={cn(
        'group/sort inline-flex items-center gap-1 rounded-xs',
        'transition-colors duration-(--m-fast) hover:text-primary',
        'focus-visible:outline-none focus-visible:shadow-(--focus-ring)',
        column.align === 'right' && 'flex-row-reverse',
        direction && 'text-primary',
      )}
      title={
        direction === 'asc' ? t('table.sortAscending')
        : direction === 'desc' ? t('table.sortDescending')
        : t('table.sortNone')
      }
    >
      <span>{column.header}</span>
      <IconArrowUp
        size={12}
        aria-hidden="true"
        className={cn(
          'shrink-0 transition-[transform,opacity] duration-(--m-fast)',
          direction === 'desc' && 'rotate-180',
          direction ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-40',
        )}
      />
    </button>
  );
}
